import React, {FC} from "react";
import {shallowEqual, useSelector} from "react-redux";
import {AppState} from "../../AppState";
import PasswordDialog from "../common/PasswordDialog";
import {isDbDraftEmail, isEmailContent, isPgpKeyContent} from "../../domain/ItemContent";
import {handleViewEncryptedEmailContent} from "../../security/EncryptedEmailProcessor";
import {AccountsActions} from "../../redux/AccountsSlice";
import {ItemActions} from "../../actions/ItemActions";
import {ContentLocation} from "../../locations/ContentLocation";
import {useAppDispatch, useAppStore} from "../../hooks/ReduxHooks";
import {isGateKeeperRecordDto} from "../../services/messages/GateKeeperRecordDto";
import {ProtectedFilesService} from "../../services/protected/ProtectedFilesService";
import {ComposeEmailActions} from "../../actions/ComposeEmailActions";
import {signAndStorePgpKey} from "../pgpkeys/PgpKeyActionsView";
import {findGatekeeperSecretKey} from "../../util/GateKeeperXmlParser";

export const EncryptionPasswordDialog: FC = () => {

    const {keyDescription, passwordError, passwordRequired, passwordRequiredForItem} =
        useSelector(({accountsState}: AppState) => ({
            keyDescription: accountsState.keyDescription,
            passwordError: accountsState.passwordError,
            passwordRequired: accountsState.passwordRequired,
            passwordRequiredForItem: accountsState.passwordRequiredForItem,
        }), shallowEqual);

    const dispatch = useAppDispatch();
    const store = useAppStore();

    let message;
    if (isEmailContent(passwordRequiredForItem)) {
        message = "Please enter the password for the decrypting this email"
    } else if (isDbDraftEmail(passwordRequiredForItem)) {
        message = "Please enter the password for signing this email";
    } else if (isPgpKeyContent(passwordRequiredForItem)) {
        message = "Please enter the password for signing this key";
    } else {
        message = `Please enter the password for the decrypting "${passwordRequiredForItem.Name}"`;
    }

    async function onEnterPassword(password: string) {
        dispatch(AccountsActions.setAccountsPassword(password));

        if (isEmailContent(passwordRequiredForItem)) {
            await handleViewEncryptedEmailContent(passwordRequiredForItem, dispatch);
        } else if (isGateKeeperRecordDto(passwordRequiredForItem)) {
            ContentLocation.clearItemContent();
            ContentLocation.showAccount(passwordRequiredForItem.Id, passwordRequiredForItem.ClientId);
        } else if (isDbDraftEmail(passwordRequiredForItem)) {
            const state = store.getState();
            const draftEmail = JSON.parse(JSON.stringify(passwordRequiredForItem));
            await ComposeEmailActions.sendEmail(draftEmail, state.system.isOffline, store);
        } else if (isPgpKeyContent(passwordRequiredForItem)) {
            const {system, accountsState} = store.getState();
            const secretKeys = findGatekeeperSecretKey(accountsState.secretKeys, [system.defaultAddress]);
            await signAndStorePgpKey(passwordRequiredForItem, secretKeys, accountsState.password, dispatch);
        } else {
            const {accountsState} = store.getState();
            await ProtectedFilesService.downloadFile(passwordRequiredForItem, accountsState.secretKeys, accountsState.password, dispatch);
        }
    }

    function onCancelPasswordDialog() {
        dispatch(ItemActions.cancelPasswordEntry());
    }

    return (
        <PasswordDialog
            title="Password Required"
            message={message}
            details={`Key: ${keyDescription}`}
            errorMessage={passwordError}
            open={passwordRequired}
            onCancel={onCancelPasswordDialog}
            onEnterPassword={onEnterPassword}
        />
    );
};